import { Button, FormField, KInput, Spinner, View, type StackProps } from '@kit/ui'
import { Eye, EyeClosed } from '@tamagui/lucide-icons'
import { useForm, useStore } from '@tanstack/react-form'
import { useState } from 'react'
import { z } from 'zod'

type SignInFormProps = {
  defaultValues?: {
    username?: string
    password?: string
  }
  submitText?: string
  onSubmit: (data: { username: string; password: string }) => Promise<void>
  onForgotPassword?: () => void
} & Omit<StackProps, 'children'>

const SignInSchema = z.object({
  username: z.string().min(1, 'Usuario requerido'),
  password: z.string().min(1, 'Contraseña requerida'),
})

export function SignInForm({
  defaultValues,
  submitText = 'Ingresar',
  onSubmit,
  onForgotPassword,
  ...props
}: SignInFormProps) {
  const [securePassword, setSecurePassword] = useState(true)
  const form = useForm({
    defaultValues: {
      username: defaultValues?.username ?? '',
      password: defaultValues?.password ?? '',
    } as z.infer<typeof SignInSchema>,
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })

  const { canSubmit, isSubmitting } = useStore(form.store, ({ canSubmit, isSubmitting }) => ({
    canSubmit,
    isSubmitting,
  }))

  return (
    <View gap="$true" {...props}>
      <form.Field
        name="username"
        children={(field) => (
          <FormField errors={field.state.meta.errors}>
            <KInput
              placeholder="Usuario"
              autoCapitalize="none"
              value={field.state.value}
              onChangeText={field.handleChange}
              onBlur={field.handleBlur}
            />
          </FormField>
        )}
      />
      <form.Field
        name="password"
        children={(field) => (
          <FormField errors={field.state.meta.errors}>
            <View pos="relative" jc="center">
              <KInput
                placeholder="Contraseña"
                autoCapitalize="none"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                secureTextEntry={securePassword}
                // Añadimos padding a la derecha para que el texto no choque con el ojo
                pr={50}
              />
              {/* 2. El botón ahora se posiciona respecto a este View central */}
              <View pos="absolute" r={4} zi={10}>
                <Button
                  chromeless // Si tu librería lo soporta para quitar bordes
                  onPress={() => setSecurePassword(!securePassword)}
                  scale={0.8} // Ajustamos un poco el tamaño
                  bg="transparent"
                  pressStyle={{ bc: 'transparent' }}
                >
                  <Button.Icon scaleIcon={1.2}>
                    {(!securePassword && <Eye />) || <EyeClosed />}
                  </Button.Icon>
                </Button>
              </View>
            </View>
          </FormField>
        )}
      />
      {/* Olvidé mi contraseña — alineado a la derecha, sutil */}
      <View ai="flex-end" mt="$-2">
        <Button
          chromeless
          size="$2"
          bg="transparent"
          pressStyle={{ bg: 'transparent', o: 0.6 }}
          hoverStyle={{ bg: 'transparent', o: 0.8 }}
          onPress={onForgotPassword}
        >
          <Button.Text
            fontSize={13}
            color="$blue10"
            textDecorationLine="underline"
            textDecorationColor="$blue6"
          >
            ¿Olvidaste tu contraseña?
          </Button.Text>
        </Button>
      </View>

      <View ai={'center'}>
        <Button
          disabledStyle={{
            o: 0.7,
          }}
          disabled={!canSubmit}
          onPress={form.handleSubmit}
        >
          {isSubmitting && (
            <Button.Icon>
              <Spinner />
            </Button.Icon>
          )}
          <Button.Text>{submitText}</Button.Text>
        </Button>
      </View>
    </View>
  )
}
